import { useState, useEffect } from 'react';
import { WifiOff, Wifi, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface OfflineIndicatorProps {
  className?: string;
  position?: 'top' | 'bottom';
  showReconnecting?: boolean;
}

const OfflineIndicator = ({
  className,
  position = 'bottom',
  showReconnecting = true,
}: OfflineIndicatorProps) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [showBanner, setShowBanner] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setIsReconnecting(false);
      // Keep banner visible briefly to show reconnection success
      setTimeout(() => setShowBanner(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowBanner(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Periodically check connection when offline
  useEffect(() => {
    if (!isOnline && showReconnecting) {
      const interval = setInterval(async () => {
        setIsReconnecting(true);
        try {
          // Try to fetch a small resource to check connectivity
          const response = await fetch('/favicon.ico', {
            method: 'HEAD',
            cache: 'no-store',
          });
          if (response.ok) {
            setIsOnline(true);
            setTimeout(() => setShowBanner(false), 3000);
          }
        } catch {
          // Still offline
        }
        setIsReconnecting(false);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [isOnline, showReconnecting]);

  if (!showBanner) return null;

  return (
    <div
      className={cn(
        "fixed left-0 right-0 z-[100] px-4 py-3 text-sm font-medium transition-all duration-300",
        position === 'top' ? 'top-0' : 'bottom-0',
        isOnline
          ? 'bg-green-500 text-white'
          : 'bg-amber-500 text-amber-950',
        className
      )}
    >
      <div className="container flex items-center justify-center gap-2">
        {isOnline ? (
          <>
            <Wifi className="h-4 w-4" />
            <span>Ligação restabelecida</span>
          </>
        ) : (
          <>
            {isReconnecting ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <WifiOff className="h-4 w-4" />
            )}
            <span>
              {isReconnecting
                ? 'A tentar reconectar...'
                : 'Sem ligação à internet. Algumas funcionalidades podem não estar disponíveis.'}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

// Hook to check online status
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

export default OfflineIndicator;
